import { useMemo, useState } from 'react';
import { Refresh, Table } from '@react-apps/design-system';
import type { ColumnDef } from '@react-apps/design-system';
import type { EmailMessage } from '../../domain/entities/notify.entity';
import {
  EmailStatusIcon,
  EmailTypeBadge,
  ErrorText,
  formatDateTime,
  getApiErrorMessage,
} from '../display';
import {
  useNotifyEmails,
  useRetryFailedEmail,
} from '../hooks/use-notify-emails';
import { ConfirmModal } from '../components/ConfirmModal';
import { RefreshButton } from '@/shared/RefreshButton';
import { Tooltip } from '@/shared/components/tooltip';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';

const PER_PAGE = 10;

export function NotifyEmailsPage() {
  // Página vive na URL (não em useState) para sobreviver a remount — ver
  // primum-core-root/docs/plans/done/admin-dashboard-pagination-url-sync.md.
  const [page, setPage] = usePageQueryParam();
  const [retrying, setRetrying] = useState<EmailMessage | null>(null);

  const { data, isLoading, refetch, isFetching } = useNotifyEmails({
    page,
    perPage: PER_PAGE,
  });
  const retryEmail = useRetryFailedEmail();

  const emails = useMemo(() => data?.items ?? [], [data?.items]);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  const handleConfirmRetry = () => {
    if (!retrying) return;
    retryEmail.mutate(
      { id: retrying.id, body: { reason: 'Retry manual pelo admin' } },
      { onSuccess: () => setRetrying(null) },
    );
  };

  const columns: ColumnDef<EmailMessage>[] = [
    {
      key: 'recipient',
      header: 'Destinatário/Tipo',
      render: (row) => (
        <div className="space-y-1">
          <p className="text-(--color-text-primary) text-sm">{row.recipient}</p>
          <EmailTypeBadge type={row.type} />
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <EmailStatusIcon email={row} />,
    },
    {
      key: 'attempts',
      header: 'Tentativas',
      render: (row) => (
        <span className="text-(--color-text-secondary)">{row.attempts}</span>
      ),
    },
    {
      key: 'sentAt',
      header: 'Enviado em',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.sentAt)}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Criado em',
      render: (row) => (
        <span className="text-(--color-text-secondary)">
          {formatDateTime(row.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) =>
        row.status === 'FAILED' ? (
          <div className="flex justify-end">
            <Tooltip message="Reprocessar envio" trigger="hover">
              <button
                type="button"
                onClick={() => {
                  retryEmail.reset();
                  setRetrying(row);
                }}
                aria-label="Reprocessar envio"
                className="text-(--color-text-secondary) hover:text-(--color-text-primary) hover:bg-(--color-bg-secondary) rounded-md p-1.5 transition"
              >
                <Refresh size="sm" />
              </button>
            </Tooltip>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-(--color-text-primary) text-2xl font-semibold">
            Notify · E-mails
          </h1>
          <p className="text-(--color-text-secondary) mt-1 max-w-3xl text-sm">
            Histórico de e-mails transacionais enviados pela plataforma
            (primeiro acesso, redefinição de senha, verificação de e-mail).
          </p>
        </div>
        <RefreshButton
          onRefresh={() => void refetch()}
          isRefreshing={isFetching}
        />
      </div>

      <ConfirmModal
        open={!!retrying}
        onClose={() => setRetrying(null)}
        onConfirm={handleConfirmRetry}
        title="Reprocessar envio"
        description={
          retrying
            ? `Reprocessar o envio deste e-mail para ${retrying.recipient}?`
            : undefined
        }
        confirmLabel="Reprocessar"
        loading={retryEmail.isPending}
        error={retryEmail.error}
      />

      <div className="border-(--color-border) bg-(--color-bg-primary) space-y-5 rounded-xl border p-2 sm:p-6">
        {retryEmail.isError && !retrying && (
          <ErrorText>
            {getApiErrorMessage(
              retryEmail.error,
              'Falha ao reprocessar e-mail',
            )}
          </ErrorText>
        )}

        <Table
          columns={columns}
          data={emails}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="Nenhum e-mail encontrado."
          striped
        />

        <div className="flex items-center justify-between">
          <p className="text-(--color-text-secondary) text-xs">
            {data
              ? `${data.total} ${data.total === 1 ? 'e-mail encontrado' : 'e-mails encontrados'}`
              : null}
          </p>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Anterior
              </button>
              <span className="text-(--color-text-secondary) text-xs">
                {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages || isLoading}
                className="border-(--color-border) text-(--color-text-secondary) hover:bg-(--color-bg-secondary) rounded-lg border px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
              >
                Próxima
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
